// @ts-strict-ignore
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { v4 as uuid } from 'uuid';
import {
  CreateOrUpdateNodeRequest,
  CreateTargetRequest,
  DeleteNodeResponse,
  Indicator,
  NodeSummary,
} from '../../api/boards/models';
import { MigrateBoardRequest } from '../../api/boards/models/migrate-board-request';
import { BoardsService, IndicatorsService } from '../../api/boards/services';
import { createEmptyIndicator } from '../../feature/editor-transition-hierarchy/functions/create-empty-indicator';
import { conditionallyRemoveFilesUrlPrefixOnImages } from '../../functions/utilities/img-url-utility';
import { BoardMigrationStatus } from '../../model/board-migration-status';
import { ClientIndicator } from '../../model/client-model/client-indicator';
import { ClientNode } from '../../model/client-model/client-node';
import { ExtendedDescription } from '../../model/client-model/extended-description/extended-description';
import { NodeListItem } from '../../model/node-list-item';
import { AppConfigService } from '../app-config.service';
import { ModelService } from '../backend-service/model.service';
import { toArray } from './backend-utility';

@Injectable({ providedIn: 'root' })
export class BackendBoardsService {
  constructor(
    private _config: AppConfigService,
    private boardsService: BoardsService,
    private indicatorsService: IndicatorsService,
    private modelService: ModelService
  ) {}

  addTransitionElement(
    organisationId: string,
    boardId: string,
    node: ClientNode,
    parentId?: string
  ): Observable<{ node: ClientNode & { parentETag?: string }; indicator: ClientIndicator }> {
    const indicator: Indicator = { ...createEmptyIndicator(), id: uuid(), _etag: undefined };
    const body: CreateTargetRequest = { node: { ...node, parentId }, indicator };
    return this.boardsService.addTarget({ organisationId, boardId, body }).pipe(
      map(({ node, indicator }) => {
        return {
          node: { ...this.modelService.toClientNode(node), parentETag: node.parentETag },
          indicator: this.modelService.toClientIndicator(indicator),
        };
      })
    );
  }

  createOrUpdateNode(
    organisationId: string,
    boardGuid: string,
    node: ClientNode,
    parentId?: string
  ): Observable<ClientNode & { parentETag?: string }> {
    const body: CreateOrUpdateNodeRequest = this._createOrUpdateNodeFromClientNode(node, parentId);
    return this.boardsService
      .addOrUpdateNode({ organisationId, boardGuid, nodeGuid: node.id, body })
      .pipe(map((node) => ({ ...this.modelService.toClientNode(node), parentETag: node.parentETag })));
  }

  deleteBoard(boardId: string, organisationId: string): Observable<void> {
    return this.boardsService.deleteBoard({ boardId, organisationId });
  }

  deleteNode(
    organisationId: string,
    boardId: string,
    nodeId: string,
    _etag: string,
    recursive?: boolean
  ): Observable<DeleteNodeResponse> {
    return this.boardsService.deleteNode({
      _etag,
      boardId,
      nodeId,
      organisationId,
      recursive,
    });
  }

  getBoardMigrationStatus(boardId: string): Observable<BoardMigrationStatus> {
    return this.boardsService.getBoardMigrationStatus({ boardId }).pipe(map(({ migrationStatus }) => migrationStatus));
  }

  /**
   * @deprecated
   * @param organisationId
   * @param boardId
   * @returns
   */
  getNodeListItems(organisationId, boardId): Observable<NodeListItem[]> {
    return this.boardsService.getNodes({ organisationId, boardId }).pipe(
      map((result) => toArray(result)),
      map((nodes) => {
        const nodeIds = nodes.map((n) => n.id);
        return nodes.map(
          (node) =>
            <NodeListItem>{
              _etag: node._etag,
              _ts: node._ts,
              actions: node.actions,
              //Filter out any reference in childNodes that doesn't have a corresponding node.
              childNodes: node.childNodes.filter((id) => nodeIds.includes(id)),
              id: node.id,
              indicators: node.indicators,
              nodeProperties: node.nodeProperties,
              title: node.title,
              boardId: node.boardId,
            }
        );
      })
    );
  }

  getNodes(organisationId: string, boardId: string): Observable<ClientNode[]> {
    return this._getNodesData(organisationId, boardId).pipe(
      map((nodes) => nodes.map((node) => this.modelService.toClientNode(node)))
    );
  }

  migrateBoard({ boardId, migrationsToRun, dryRun }: MigrateBoardRequest): Observable<any> {
    return this.boardsService
      .migrateBoard({ boardId, migrationsToRun, dryRun })
      .pipe(map(({ migrationResults }) => migrationResults));
  }

  updateNode(boardId: string, userOrganisationId: string, node: NodeListItem): Observable<ClientNode> {
    const extendedDescription = this._handleExtendedDescriptionBeforeSave(node.nodeProperties.extendedDescription);
    return this.boardsService
      .updateNode({
        organisationId: userOrganisationId,
        boardId: boardId,
        nodeId: node.id,
        body: {
          _etag: node._etag,
          actions: node.actions,
          childNodes: node.childNodes,
          id: node.id,
          indicators: node.indicators,
          nodeProperties: { ...node.nodeProperties, extendedDescription },
          title: node.title,
        },
      })
      .pipe(map((node) => this.modelService.toClientNode(node)));
  }

  private _createOrUpdateNodeFromClientNode(node: ClientNode, parentId: string): CreateOrUpdateNodeRequest {
    if (node.nodeProperties.extendedDescription?.data.images) {
      node.nodeProperties.extendedDescription.data.images = node.nodeProperties.extendedDescription.data.images.map(
        (image) => ({
          ...image,
          imgURL: conditionallyRemoveFilesUrlPrefixOnImages(image.imgURL, this._config.baseUrlFiles),
        })
      );
    }
    return {
      _etag: node._etag,
      actions: node.actions,
      childNodes: node.childNodes,
      indicators: node.indicators,
      nodeProperties: node.nodeProperties,
      title: node.title,
      parentId,
    };
  }

  private _getNodesData(organisationId: string, boardId: string): Observable<NodeSummary[]> {
    return this.boardsService.getNodes({ organisationId, boardId }).pipe(
      map((result) => toArray(result)),
      map((nodes) => {
        const nodeIds = nodes.map((n) => n.id);
        return nodes.map((node) => {
          // Filter out any reference in childNodes that doesn't have a corresponding node.
          let filtered: NodeSummary = {
            ...node,
            childNodes: (node.childNodes ?? []).filter((id) => nodeIds.includes(id)),
          };
          return filtered;
        });
      })
    );
  }

  private _handleExtendedDescriptionBeforeSave(extendedDescription: ExtendedDescription): ExtendedDescription {
    if (extendedDescription?.data?.images) {
      extendedDescription.data.images = extendedDescription.data.images.map((image) => ({
        ...image,
        imgURL: conditionallyRemoveFilesUrlPrefixOnImages(image.imgURL, this._config.baseUrlFiles),
      }));
    }
    return extendedDescription;
  }
}
